import type { ReactNode } from 'react';
import type { BaseProps } from 'src/modules/shared/types/component.type';
import { getDisabledClasses } from 'src/modules/shared/styles/component.styles';
import { useTabsContext } from './tabs';
import {
  tabBaseClasses,
  tabSizeClasses,
  tabAppearanceClasses,
  tabActiveClasses,
} from 'src/modules/shared/components/atoms/tabs/tab.styles';
import { Text } from 'src/modules/shared/components/atoms/text';
import { getControlTextSize } from 'src/modules/shared/components/atoms/text/text.styles';

export interface TabProps
  extends Omit<BaseProps, 'variant' | 'size' | 'disabled'> {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

/**
 * Tab component for use within the shared Tabs.
 *
 * Usage:
 * ```tsx
 * <Tabs value={activeTab} onChange={setActiveTab}>
 *   <Tab value="general" label="General" />
 *   <Tab value="communications" label="Communications" icon={<Icon />} />
 * </Tabs>
 * ```
 */
export const Tab = ({
  value,
  label,
  icon,
  className = '',
  disabled = false,
}: TabProps) => {
  const {
    value: selectedValue,
    onChange,
    disabled: tabsDisabled,
    variant,
    appearance,
    size,
    orientation,
    allowWrap,
    scrollable,
    align,
  } = useTabsContext();

  const isActive = selectedValue === value;
  const isDisabled = disabled || tabsDisabled;

  const handleClick = () => {
    if (!isDisabled && !isActive) {
      onChange(value);
    }
  };

  const getFlexClasses = () => {
    if (orientation === 'vertical') {
      return 'flex-1 w-full';
    }

    if (allowWrap || scrollable) {
      return 'flex-none min-w-fit';
    }

    return 'flex-1';
  };

  const getAlignmentClasses = (): string => {
    const alignMap: Record<string, string> = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
    };
    return alignMap[align] || 'justify-center';
  };

  const classes = [
    ...tabBaseClasses,
    getAlignmentClasses(),
    tabSizeClasses[size],
    tabAppearanceClasses[appearance][variant],
    isActive && tabActiveClasses[appearance][variant],
    getDisabledClasses(isDisabled),
    getFlexClasses(),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      role="tab"
      aria-selected={isActive}
      aria-controls={`tabpanel-${value}`}
      disabled={isDisabled}
      onClick={handleClick}
      className={classes}
      type="button"
    >
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <Text
          size={getControlTextSize(size)}
          weight={isActive ? 'medium' : 'regular'}
          className="truncate"
        >
          {label}
        </Text>
      </div>
    </button>
  );
};
